/**
 * Layout inference for mosaic drag-and-drop with shared edge detection.
 *
 * This module detects when widgets form columns or rows (via shared edges)
 * and provides infrastructure for N-way space redistribution when dropping
 * into these structures.
 */
import type { NormalizedLayout } from './mosaic-drop-targets';

/** Tolerance for floating-point comparisons in layout positions (matches grid snap size) */
const POSITION_TOLERANCE = 0.01;

/**
 * A shared edge between two adjacent widgets.
 * Two widgets share an edge when they are directly adjacent and have
 * the same extent in the perpendicular direction.
 */
export interface SharedEdge {
    /** Whether the edge is horizontal (top/bottom) or vertical (left/right) */
    orientation: 'horizontal' | 'vertical';
    /** y-position for horizontal edges, x-position for vertical edges */
    position: number;
    /** Start position on perpendicular axis */
    start: number;
    /** End position on perpendicular axis */
    end: number;
    /** Widget on the negative side (above for horizontal, left for vertical) */
    widgetA: string;
    /** Widget on the positive side (below for horizontal, right for vertical) */
    widgetB: string;
}

/**
 * An inferred column - a maximal set of vertically stacked widgets.
 * All widgets have the same x position and width, and consecutive
 * widgets share horizontal edges.
 */
export interface InferredColumn {
    /** Widget IDs in top-to-bottom order */
    widgetIds: string[];
    /** Shared x position (left edge) */
    x: number;
    /** Shared x + width (right edge) */
    xEnd: number;
    /** Internal shared edges between consecutive widgets */
    internalEdges: SharedEdge[];
}

/**
 * An inferred row - a maximal set of horizontally arranged widgets.
 * All widgets have the same y position and height, and consecutive
 * widgets share vertical edges.
 */
export interface InferredRow {
    /** Widget IDs in left-to-right order */
    widgetIds: string[];
    /** Shared y position (top edge) */
    y: number;
    /** Shared y + height (bottom edge) */
    yEnd: number;
    /** Internal shared edges between consecutive widgets */
    internalEdges: SharedEdge[];
}

/**
 * Complete layout inference state for a set of widget layouts.
 */
export interface LayoutInferenceState {
    /** All shared edges found between widget pairs */
    sharedEdges: SharedEdge[];
    /** All inferred columns (maximal vertical groupings) */
    columns: InferredColumn[];
    /** All inferred rows (maximal horizontal groupings) */
    rows: InferredRow[];
}

/**
 * Check if two values are approximately equal within tolerance.
 */
function approxEqual(a: number, b: number, tolerance = POSITION_TOLERANCE): boolean {
    return Math.abs(a - b) < tolerance;
}

/**
 * Find all shared edges between widget pairs in the layouts.
 *
 * Two widgets share a horizontal edge when:
 * - A's bottom edge touches B's top edge (or vice versa)
 * - They have the same x position and width
 *
 * Two widgets share a vertical edge when:
 * - A's right edge touches B's left edge (or vice versa)
 * - They have the same y position and height
 */
export function findSharedEdges(layouts: Record<string, NormalizedLayout>): SharedEdge[] {
    const sharedEdges: SharedEdge[] = [];
    const widgetIds = Object.keys(layouts);

    for (let i = 0; i < widgetIds.length; i++) {
        for (let j = i + 1; j < widgetIds.length; j++) {
            const idA = widgetIds[i];
            const idB = widgetIds[j];
            const layoutA = layouts[idA];
            const layoutB = layouts[idB];

            // Check horizontal edge (A above B)
            if (
                approxEqual(layoutA.y + layoutA.h, layoutB.y) &&
                approxEqual(layoutA.x, layoutB.x) &&
                approxEqual(layoutA.x + layoutA.w, layoutB.x + layoutB.w)
            ) {
                sharedEdges.push({
                    orientation: 'horizontal',
                    position: layoutB.y,
                    start: layoutA.x,
                    end: layoutA.x + layoutA.w,
                    widgetA: idA,
                    widgetB: idB,
                });
            }
            // Check horizontal edge (B above A)
            else if (
                approxEqual(layoutB.y + layoutB.h, layoutA.y) &&
                approxEqual(layoutA.x, layoutB.x) &&
                approxEqual(layoutA.x + layoutA.w, layoutB.x + layoutB.w)
            ) {
                sharedEdges.push({
                    orientation: 'horizontal',
                    position: layoutA.y,
                    start: layoutA.x,
                    end: layoutA.x + layoutA.w,
                    widgetA: idB,
                    widgetB: idA,
                });
            }

            // Check vertical edge (A left of B)
            if (
                approxEqual(layoutA.x + layoutA.w, layoutB.x) &&
                approxEqual(layoutA.y, layoutB.y) &&
                approxEqual(layoutA.y + layoutA.h, layoutB.y + layoutB.h)
            ) {
                sharedEdges.push({
                    orientation: 'vertical',
                    position: layoutB.x,
                    start: layoutA.y,
                    end: layoutA.y + layoutA.h,
                    widgetA: idA,
                    widgetB: idB,
                });
            }
            // Check vertical edge (B left of A)
            else if (
                approxEqual(layoutB.x + layoutB.w, layoutA.x) &&
                approxEqual(layoutA.y, layoutB.y) &&
                approxEqual(layoutA.y + layoutA.h, layoutB.y + layoutB.h)
            ) {
                sharedEdges.push({
                    orientation: 'vertical',
                    position: layoutA.x,
                    start: layoutA.y,
                    end: layoutA.y + layoutA.h,
                    widgetA: idB,
                    widgetB: idA,
                });
            }
        }
    }

    return sharedEdges;
}

/**
 * Build an adjacency map from shared edges.
 * Returns a map where each widget ID maps to the set of widgets it shares edges with.
 */
function buildAdjacencyMap(
    sharedEdges: SharedEdge[],
    orientation: 'horizontal' | 'vertical',
): Map<string, Set<string>> {
    const adjacency = new Map<string, Set<string>>();

    for (const edge of sharedEdges) {
        if (edge.orientation !== orientation) continue;

        if (!adjacency.has(edge.widgetA)) {
            adjacency.set(edge.widgetA, new Set());
        }
        if (!adjacency.has(edge.widgetB)) {
            adjacency.set(edge.widgetB, new Set());
        }
        adjacency.get(edge.widgetA)!.add(edge.widgetB);
        adjacency.get(edge.widgetB)!.add(edge.widgetA);
    }

    return adjacency;
}

/**
 * Find connected components using BFS.
 */
function findConnectedComponents(adjacency: Map<string, Set<string>>): string[][] {
    const visited = new Set<string>();
    const components: string[][] = [];

    for (const startNode of adjacency.keys()) {
        if (visited.has(startNode)) continue;

        const component: string[] = [];
        const queue = [startNode];

        while (queue.length > 0) {
            const node = queue.shift()!;
            if (visited.has(node)) continue;

            visited.add(node);
            component.push(node);

            const neighbors = adjacency.get(node);
            if (neighbors) {
                for (const neighbor of neighbors) {
                    if (!visited.has(neighbor)) {
                        queue.push(neighbor);
                    }
                }
            }
        }

        if (component.length > 1) {
            components.push(component);
        }
    }

    return components;
}

/**
 * Infer columns from horizontal shared edges.
 *
 * A column is a maximal set of widgets where:
 * - All have the same x position and width
 * - Consecutive widgets share horizontal edges (stacked vertically)
 */
export function inferColumns(layouts: Record<string, NormalizedLayout>, sharedEdges: SharedEdge[]): InferredColumn[] {
    const horizontalEdges = sharedEdges.filter((e) => e.orientation === 'horizontal');
    const adjacency = buildAdjacencyMap(horizontalEdges, 'horizontal');
    const components = findConnectedComponents(adjacency);
    const columns: InferredColumn[] = [];

    for (const component of components) {
        // Sort by y position (top to bottom)
        component.sort((a, b) => layouts[a].y - layouts[b].y);

        // Verify all widgets have same x extent
        const firstLayout = layouts[component[0]];
        const x = firstLayout.x;
        const xEnd = firstLayout.x + firstLayout.w;

        const allSameExtent = component.every((id) => {
            const layout = layouts[id];
            return approxEqual(layout.x, x) && approxEqual(layout.x + layout.w, xEnd);
        });

        if (!allSameExtent) continue;

        // Collect internal edges in order
        const internalEdges: SharedEdge[] = [];
        for (let i = 0; i < component.length - 1; i++) {
            const edge = horizontalEdges.find(
                (e) =>
                    (e.widgetA === component[i] && e.widgetB === component[i + 1]) ||
                    (e.widgetA === component[i + 1] && e.widgetB === component[i]),
            );
            if (edge) {
                internalEdges.push(edge);
            }
        }

        columns.push({
            widgetIds: component,
            x,
            xEnd,
            internalEdges,
        });
    }

    return columns;
}

/**
 * Infer rows from vertical shared edges.
 *
 * A row is a maximal set of widgets where:
 * - All have the same y position and height
 * - Consecutive widgets share vertical edges (side by side)
 */
export function inferRows(layouts: Record<string, NormalizedLayout>, sharedEdges: SharedEdge[]): InferredRow[] {
    const verticalEdges = sharedEdges.filter((e) => e.orientation === 'vertical');
    const adjacency = buildAdjacencyMap(verticalEdges, 'vertical');
    const components = findConnectedComponents(adjacency);
    const rows: InferredRow[] = [];

    for (const component of components) {
        // Sort by x position (left to right)
        component.sort((a, b) => layouts[a].x - layouts[b].x);

        // Verify all widgets have same y extent
        const firstLayout = layouts[component[0]];
        const y = firstLayout.y;
        const yEnd = firstLayout.y + firstLayout.h;

        const allSameExtent = component.every((id) => {
            const layout = layouts[id];
            return approxEqual(layout.y, y) && approxEqual(layout.y + layout.h, yEnd);
        });

        if (!allSameExtent) continue;

        // Collect internal edges in order
        const internalEdges: SharedEdge[] = [];
        for (let i = 0; i < component.length - 1; i++) {
            const edge = verticalEdges.find(
                (e) =>
                    (e.widgetA === component[i] && e.widgetB === component[i + 1]) ||
                    (e.widgetA === component[i + 1] && e.widgetB === component[i]),
            );
            if (edge) {
                internalEdges.push(edge);
            }
        }

        rows.push({
            widgetIds: component,
            y,
            yEnd,
            internalEdges,
        });
    }

    return rows;
}

/**
 * Compute the complete layout inference state for a set of widget layouts.
 * This is the main entry point for layout inference.
 *
 * @param layouts - Widget layouts keyed by container ID
 * @returns Complete inference state including shared edges, columns, and rows
 */
export function computeLayoutInferenceState(layouts: Record<string, NormalizedLayout>): LayoutInferenceState {
    const sharedEdges = findSharedEdges(layouts);
    const columns = inferColumns(layouts, sharedEdges);
    const rows = inferRows(layouts, sharedEdges);

    return { sharedEdges, columns, rows };
}
